/**
 * component-text.de — the German wording for the strings a schmancy component
 * renders on its own behalf: preset chips, panel field labels, aria
 * descriptions, live-region announcements, validation messages, checkout
 * chrome.
 *
 * Its own export rather than a branch of `admin`/`storefront`, because these
 * are the library's flat dotted keys (`dateRange.from`), not this repo's
 * `MessageKey` union — nothing here resolves through `translate`, and the whole
 * table is handed to `setComponentText` in one call.
 *
 * No `component-text.en.ts` beside it: the library ships the English itself and
 * `setComponentText({})` restores it, so an English table here would be a second
 * copy of words schmancy already owns, free to drift from them.
 *
 * This package declares no schmancy dependency — it is the side-neutral catalog
 * both the browser and the functions workspace read — so the key set is checked
 * where the table meets the library, in `@momo/views`' `i18n/localize.ts`. A key
 * schmancy adds, or one misspelled here, fails that package's typecheck rather
 * than rendering an English word inside a German panel.
 *
 * Register follows the surface a string renders on, not this table. A string
 * that reaches only the staff forms is Sie, calibrated against `admin.de.ts` —
 * the date pickers those belong to sit beside `admin.events.form.validation`,
 * whose own required-field messages read "Wählen Sie …". A string that reaches
 * only a buyer's checkout is Du, calibrated against `storefront.de.ts`; the
 * payment notices at the foot of this file are that case. A string that reaches
 * both apps addresses nobody, because no one sentence holds the admin's Sie and
 * the storefront's Du at once — which is what `formField.error.*` and
 * `payment.email.*` are.
 */
export const componentTextDe = {
	"autocomplete.search": "Optionen suchen",
	"autocomplete.cancel": "Abbrechen",
	"autocomplete.apply": "Übernehmen",
	"autocomplete.empty": "Keine Treffer für „{{query}}“",
	"autocomplete.required": "Bitte eine Option wählen.",

	// <schmancy-pdf-thumbnail>'s fallback caption, worded like
	// `lightbox.unavailable` below — the same "the file is fine, the picture of
	// it is not" statement, one noun apart.
	"pdfThumbnail.unavailable": "Vorschau nicht verfügbar",

	// The sort control's two directions. The bare pair labels the control; the
	// Choice pair is one option's whole accessible name, so the direction is
	// lowercase there — an adjective inside a sentence, exactly as
	// `calendar.day.selected` sets it.
	"sortPicker.ascending": "Aufsteigend",
	"sortPicker.descending": "Absteigend",
	"sortPicker.ascendingChoice": "{{field}}, aufsteigend",
	"sortPicker.descendingChoice": "{{field}}, absteigend",

	"dateRange.placeholder": "Zeitraum wählen",
	"dateRange.trigger": "Zeitraum wählen. Aktuell: {{range}}",
	// A translation is free to rebuild the sentence around the placeholder:
	// {{range}} is the range on screen NOW, so German names the target relative
	// to it rather than filling the English frame word-for-word.
	"dateRange.previous": "Zeitraum davor: {{range}}",
	"dateRange.next": "Zeitraum danach: {{range}}",
	"dateRange.announce": "Zeitraum: {{range}}",
	"dateRange.from": "Von",
	"dateRange.to": "Bis",
	// An open-ended range reads "ab" in German, never "von" — "Von 1. Aug." asks
	// the reader for a missing "bis", which is exactly the endpoint that is unset.
	"dateRange.startOnly": "Ab {{date}}",
	"dateRange.endOnly": "Bis {{date}}",
	"dateRange.preset.today": "Heute",
	"dateRange.preset.yesterday": "Gestern",
	"dateRange.preset.last7": "Letzte 7 Tage",
	"dateRange.preset.last30": "Letzte 30 Tage",
	"dateRange.preset.thisWeek": "Diese Woche",
	"dateRange.preset.thisMonth": "Dieser Monat",
	"dateRange.preset.lastMonth": "Letzter Monat",
	"dateRange.preset.thisYear": "Dieses Jahr",
	"dateRange.error.incomplete": "Wählen Sie einen vollständigen Zeitraum.",
	// "darf nicht vor … liegen", not "muss nach … liegen": the bound is
	// inclusive, and the German comparative would make it exclusive and reject a
	// same-instant start and end the field accepts.
	"dateRange.error.endBeforeStart":
		"Das Enddatum darf nicht vor dem Startdatum liegen.",
	"dateRange.error.beforeMin":
		"Das Startdatum darf nicht vor dem {{min}} liegen.",
	"dateRange.error.afterMax":
		"Das Enddatum darf nicht nach dem {{max}} liegen.",

	"dateRangeInline.error.startMissing": "Startdatum ist erforderlich",
	"dateRangeInline.error.endMissing": "Enddatum ist erforderlich",
	"dateRangeInline.error.invalidFormat": "Ungültiges Datumsformat",
	// Exclusive where the range field's key of the same name is inclusive: an
	// equal start and end is refused here — that is what sameDate below spells
	// out — so this one takes the comparative, not "darf nicht vor … liegen".
	"dateRangeInline.error.endBeforeStart":
		"Das Enddatum muss nach dem Startdatum liegen",
	"dateRangeInline.error.sameDate":
		"Start- und Enddatum dürfen nicht gleich sein",
	// "zwischen Start- und Enddatum" rather than "zwischen den Daten": in German
	// "Daten" is data, and this catalog uses it for nothing else.
	"dateRangeInline.error.minGap.one":
		"Mindestens {{count}} Tag zwischen Start- und Enddatum",
	"dateRangeInline.error.minGap.other":
		"Mindestens {{count}} Tage zwischen Start- und Enddatum",
	"dateRangeInline.error.maxGap.one":
		"Höchstens {{count}} Tag zwischen Start- und Enddatum",
	"dateRangeInline.error.maxGap.other":
		"Höchstens {{count}} Tage zwischen Start- und Enddatum",
	"dateRangeInline.error.beforeMin":
		"Das Datum liegt vor dem frühesten erlaubten Datum",
	"dateRangeInline.error.afterMax":
		"Das Datum liegt nach dem spätesten erlaubten Datum",

	"dateField.placeholder": "Datum wählen",
	"dateField.trigger": "{{label}}. Aktuell: {{date}}",
	"dateField.triggerDescribed": "{{label}}. Aktuell: {{date}}. {{description}}",
	"dateField.announce": "Datum: {{date}}",
	"dateField.input.date": "Datum",
	"dateField.input.dateTime": "Datum und Uhrzeit",
	"dateField.error.valueMissing": "Wählen Sie ein Datum.",
	// {{min}}/{{max}} arrive as the raw min/max this field was given, where the
	// range field's bound arrives formatted through Intl. "vor dem … liegen"
	// carries the article itself, so one sentence holds under either spelling.
	"dateField.error.beforeMin": "Das Datum darf nicht vor dem {{min}} liegen.",
	"dateField.error.afterMax": "Das Datum darf nicht nach dem {{max}} liegen.",

	// The calendar inside the picker's panel. Its month names and weekday
	// headers already resolve through Intl off the theme locale; these are the
	// strings it composes itself, and each owns its whole sentence rather than a
	// comma-prefixed suffix, so the German is free to punctuate its own way.
	"calendar.grid": "Kalender",
	"calendar.day.selected": "{{date}}, ausgewählt",
	// "Beginn"/"Ende des Zeitraums" and not "Start"/"Enddatum": those two name
	// the form's own fields (`dateRange.error.*` above), where these name a day's
	// role inside a range being swept. One word per job.
	"calendar.day.rangeStart": "{{date}}, Beginn des Zeitraums",
	"calendar.day.rangeEnd": "{{date}}, Ende des Zeitraums",
	// Sie-imperative, not "bitte ein Enddatum wählen": every prompt this catalog
	// renders on the same forms asks the same way ("Wählen Sie Start- und
	// Enddatum."), and the politeness "bitte" carries is what the Sie form
	// already does — which is why the family drops it.
	"calendar.day.rangeStartPending":
		"{{date}}, Beginn gewählt, wählen Sie ein Enddatum",
	"calendar.day.today": "{{date}}, heute",

	// Neither addresses the reader: one table serves the Sie admin and the Du
	// storefront at once, and these two fall back under every field in both, so
	// the Sie imperative the validation messages above take would render Sie
	// inside the buyer's checkout.
	"formField.error.valueMissing": "Dieses Feld ist erforderlich",
	"formField.error.invalid": "Ungültiger Wert",

	// The storefront hands this button its own labels with the total on them
	// ("Bezahlen · {{total}}", "Erneut versuchen · {{total}}"); these two are
	// those verbs without it, so the fallback never renames the act.
	"payment.pay": "Bezahlen",
	"payment.retry": "Erneut versuchen",
	// Spaced ellipsis to match "Aktualisiert …", the storefront's own label on
	// this same button.
	"payment.processing": "Wird verarbeitet …",
	"payment.or": "Oder",
	// Spoken only, so no ellipsis — every announcement in these catalogs drops
	// the one its visible twin carries.
	"payment.announce.updating": "Zahlungsarten werden aktualisiert",
	// "Beleg" is what this deployment already calls a payment document where the
	// buyer reads it; "Rechnung" is the order page's narrower invoice artifact
	// and would promise a document this field's address does not get.
	"payment.email.label": "E-Mail-Adresse (für den Beleg)",
	// The example address the storefront's send-by-email overlay already shows,
	// and nominal: an English "your" would have to become Ihre or deine.
	"payment.email.placeholder": "name@beispiel.de",
	"payment.email.error.required": "E-Mail-Adresse ist erforderlich",
	// Nominal, not the English imperative: asking the reader to enter something
	// would have to pick Sie or Du, and this table renders in both apps.
	"payment.email.error.invalid": "Ungültige E-Mail-Adresse",

	// The failure panel — every word a buyer reads when their card is refused.
	//
	// Du, alone in this table: these reach a buyer's checkout and nothing else,
	// and `storefront.de.ts` addresses that same buyer on that same page in Du
	// throughout. The Sie the date messages above take would seat the staff
	// register inside the buyer's failure panel.
	//
	// Voice calibrated against `storefront.de.ts`'s own `eventPage.notice.*`,
	// which covers this same union — but not copied from it: nearly every body
	// there closes on "deine Auswahl bleibt bestehen", which the library cannot
	// promise, since it does not know whether the host kept a selection. What
	// these translate is the English beside them.
	//
	// A buyer reaching for a card is "nutzen" ("Nutz eine andere Karte"); a
	// system taking a value is "verwenden" ("Ausgehende E-Mails verwenden das
	// neue Projekt-Token"), which is why contactDetails alone takes it. Bare
	// imperative and no "bitte" — the family `calendar.day.rangeStartPending`
	// above belongs to, for the reason given there.
	"payment.notice.insufficientFunds.headline":
		"Deine Karte hat nicht genug Deckung",
	// "diese Karte" rather than a pronoun: "sie" would bind to the other card
	// the same sentence just offered, naming the wrong one to top up.
	"payment.notice.insufficientFunds.body":
		"Es wurde nichts abgebucht. Nutz eine andere Karte oder lad diese Karte auf und versuch es noch einmal.",
	"payment.notice.expiredCard.headline": "Die Karte ist abgelaufen",
	"payment.notice.expiredCard.body":
		"Es wurde nichts abgebucht. Nutz eine andere Karte, um deine Bestellung abzuschließen.",
	"payment.notice.cardDetails.headline": "Einige Kartendaten stimmen nicht",
	"payment.notice.cardDetails.body":
		"Es wurde nichts abgebucht. Prüf Kartennummer, Ablaufdatum und Sicherheitscode und versuch es dann noch einmal.",
	"payment.notice.contactDetails.headline":
		"Diese Kontaktdaten konnten wir nicht verwenden",
	// "zu dieser Bestellung", where the storefront's own copy says "weiter oben":
	// the library does not know where in the host's page those two fields sit, so
	// it names the order they belong to instead of pointing at a position.
	"payment.notice.contactDetails.body":
		"Es wurde nichts abgebucht. Prüf E-Mail-Adresse und Name zu dieser Bestellung, korrigier sie und versuch es noch einmal.",
	"payment.notice.authentication.headline":
		"Deine Bank hat die Zahlung nicht bestätigt",
	"payment.notice.authentication.body":
		"Es wurde nichts abgebucht. Versuch es noch einmal und schließ die Bestätigung ab, die deine Bank dir zeigt, oder nutz eine andere Karte.",
	"payment.notice.bankDeclined.headline":
		"Deine Bank hat die Zahlung abgelehnt",
	// Passive "warum diese Karte abgelehnt wurde", not "warum sie diese
	// abgelehnt hat": the active clause carries two feminine pronouns whose
	// referents — the bank and the card — swap under the reader's eye.
	"payment.notice.bankDeclined.body":
		"Es wurde nichts abgebucht. Nutz eine andere Karte oder frag bei deiner Bank nach, warum diese Karte abgelehnt wurde.",
	"payment.notice.cardLocked.headline": "Die Karte ist gesperrt",
	// Names the PIN, where the storefront's own cardLocked deliberately does not
	// — the English here names it, and a translation that dropped it would answer
	// a different question than the sentence it stands for.
	"payment.notice.cardLocked.body":
		"Es wurde nichts abgebucht. Deine Bank hat die PIN dieser Karte gesperrt, und nur sie kann die Sperre aufheben — nutz eine andere Karte, um deine Bestellung abzuschließen.",
	"payment.notice.limitReached.headline": "Die Karte hat ein Limit erreicht",
	"payment.notice.limitReached.body":
		"Es wurde nichts abgebucht. Diese Karte hat ein Ausgabenlimit deiner Bank erreicht. Versuch es später noch einmal oder nutz eine andere Karte.",
	"payment.notice.cardNotActivated.headline":
		"Die Karte ist noch nicht freigeschaltet",
	"payment.notice.cardNotActivated.body":
		"Es wurde nichts abgebucht. Schalt die Karte bei deiner Bank frei oder nutz eine andere Karte, um deine Bestellung abzuschließen.",
	"payment.notice.cardNotSupported.headline":
		"Diese Karte lässt sich hier nicht nutzen",
	"payment.notice.cardNotSupported.body":
		"Es wurde nichts abgebucht. Nutz eine andere Karte.",
	// The card clause sits in the body here, where the storefront carries it in
	// the headline — this English splits them that way, so aligning the two
	// would say the same thing twice on one panel.
	"payment.notice.technical.headline": "Das lag an uns",
	"payment.notice.technical.body":
		"Es wurde nichts abgebucht, und an deiner Karte liegt es nicht. Versuch es gleich noch einmal.",
	"payment.notice.refused.headline": "Die Zahlung ist nicht durchgegangen",
	"payment.notice.refused.body":
		"Es wurde nichts abgebucht. Versuch es noch einmal oder nutz eine andere Karte.",
	// "Bestellung" carries both nouns this member's English uses — checkout in
	// the headline, order in the body — because the storefront renders one word
	// for the one thing, and a second would read as a second thing.
	"payment.notice.orderStale.headline": "Diese Bestellung ist nicht mehr offen",
	"payment.notice.orderStale.body":
		"Es wurde nichts abgebucht. Diese Bestellung wurde bereits abgeschlossen oder storniert, ein weiterer Versuch führt hier also zu nichts — starte eine neue Bestellung, um zu bezahlen.",
	"payment.notice.blocked.headline": "Wir können gerade keine Zahlung annehmen",
	"payment.notice.blocked.body":
		"Das Zahlungsformular konnte nicht geladen werden. Prüf deine Verbindung und versuch es erneut — es wurde nichts abgebucht.",
	// Re-loads the form, where the storefront's own remedy says "Erneut
	// versuchen": this button re-enters the widget's initialization, which is a
	// different act from retrying the payment.
	"payment.notice.blocked.remedy": "Zahlungsformular neu laden",
	// Appended to a link's own name for a screen reader, never shown — so it
	// reads as the continuation of that name rather than as a sentence.
	"link.newWindow": "wird in einem neuen Fenster geöffnet",
	// The sentence a refused file earns, at the moment it is handed over.
	// Addresses nobody — no Sie, no Du — because a file intake belongs to no one
	// app: this deployment's first is the event console's artwork upload, and a
	// buyer-facing one would arrive under the storefront's Du with these same
	// four keys. Every sentence here is therefore about the FILE and never about
	// the reader, which is also what they should be: the reader handed over
	// something of theirs and is owed a sentence about that thing.
	//
	// `{{formats}}` arrives already reduced to the words a reader has (JPEG,
	// PNG), never as the MIME list `accept` is written in.
	"dropzone.refusal.image": "{{name}} ist kein Bild.",
	"dropzone.refusal.video": "{{name}} ist kein Video.",
	"dropzone.refusal.audio": "{{name}} ist keine Audiodatei.",
	"dropzone.refusal.text": "{{name}} ist keine Textdatei.",
	"dropzone.refusal.type":
		"{{name}} ist keines der erlaubten Formate ({{formats}}).",
	// The English hangs its limit off an em-dash fragment; German makes it a
	// clause, because "über dem 50-kB-Limit" is a compound the reader has to
	// take apart to find the number that is the whole point of the sentence.
	"dropzone.refusal.tooLarge":
		"{{name}} ist {{size}} kB groß — erlaubt sind höchstens {{limit}} kB.",
	"dropzone.refusal.tooMany.one":
		"Hier passt nur eine Datei — die vorhandene zuerst entfernen.",
	"dropzone.refusal.tooMany.other": "Hier passen höchstens {{max}} Dateien.",

	// Accessible names for controls that act on a subject. German states the
	// object first and the verb as a trailing infinitive — "Berlin entfernen",
	// never "Entfernen Berlin" — so the English word order cannot be carried
	// across; a name calqued from it is heard as broken rather than merely
	// foreign. The Unnamed variants are the same control where the consumer
	// gave it no subject, so they are the bare verb and nothing else.
	// <schmancy-attachments variant="button">'s compact trigger, where the
	// dashed drop zone has no room. One image per press whatever `max` is, so
	// the noun stays singular — "Bilder anhängen" would promise a multi-select
	// the control does not offer.
	"attachments.attach": "Bild anhängen",
	"chip.remove": "{{name}} entfernen",
	"chip.removeUnnamed": "Entfernen",
	// aus-/einklappen over erweitern/reduzieren: the latter pair reads as
	// changing a thing's size, which is what a tree node's disclosure is not.
	"tree.expand": "{{name}} ausklappen",
	"tree.expandUnnamed": "Ausklappen",
	"tree.collapse": "{{name}} einklappen",
	"tree.collapseUnnamed": "Einklappen",
	// <schmancy-copy>. The two action names take the same object-first shape the
	// family above does, and the Unnamed variants the same bare verb.
	"copy.action": "{{subject}} kopieren",
	"copy.actionUnnamed": "Kopieren",
	// The confirmation the button itself shows, and the one only spoken. Both
	// are the participle rather than a sentence: the control has just been
	// pressed, so naming who did it says nothing the reader does not know.
	"copy.done": "Kopiert",
	"copy.announce": "{{subject}} kopiert",
	"copy.announceUnnamed": "Kopiert",
	// The failure carries the value, because that is the whole point of it: an
	// unreachable clipboard has to leave the thing transcribable, and a DNS
	// record typed from memory fails silently. Passive and then a bare
	// infinitive phrase — this reaches a staff form and a buyer's page alike, so
	// it addresses neither.
	"copy.failed":
		"{{subject}} konnte nicht kopiert werden — zum Abtippen: {{value}}",
	"copy.failedUnnamed": "Kopieren nicht möglich — zum Abtippen: {{value}}",
	"navigationBar.item": "Navigationselement",

	// <schmancy-install-app> / <schmancy-install-steps>. Each install route
	// keeps ITS OWN platform's German, which is the whole reason the library
	// splits these keys by route: Apple's German says „Home-Bildschirm" and
	// „Zu Home-Bildschirm hinzufügen" (Apple's iPhone user guide, de-DE) and
	// „Ablage" > „Zum Dock hinzufügen" on the Mac, while every Android browser
	// that only makes a shortcut says „Startbildschirm" — Firefox for Android's
	// own menu string is „Zum Startbildschirm hinzufügen". Carrying one of those
	// words onto another platform sends the reader hunting for a menu item that
	// is not on their screen, which is the defect this component exists to stop.
	//
	// Addresses nobody, like `formField.error.*` above: an install offer belongs
	// to no one app — it sits in a staff member's menu under Sie and in a
	// buyer's under Du with these same keys — so every step is a bare
	// infinitive, the form German device instructions already take.
	"installApp.action": "App installieren",
	// A noun, because the button opens instructions rather than installing:
	// „Installieren" on a control that installs nothing is a broken promise, and
	// the label has to stand alone in a menu row with no pill beside it.
	"installApp.actionSteps": "Installationsanleitung",
	"installApp.dismiss": "Installationshinweis ausblenden",
	"installApp.announce.browserPrompt":
		"Diese App kann auf diesem Gerät installiert werden.",
	// „lässt sich … hinzufügen" rather than the English possessive: naming the
	// reader's own Home-Bildschirm would have to pick deinen or Ihren.
	"installApp.announce.iosShare":
		"Diese App lässt sich zum Home-Bildschirm hinzufügen.",
	"installApp.announce.macosDock": "Diese App lässt sich zum Dock hinzufügen.",
	"installApp.announce.menuShortcut":
		"Diese App lässt sich zum Startbildschirm hinzufügen.",
	"installApp.steps.title": "Diese App installieren",
	"installApp.steps.subtitle": "Drei Schritte im Browser-Menü.",
	"installApp.steps.done": "Fertig",
	// The glyph sits between the two halves, so each pair breaks right before
	// the noun it marks — the mark the reader is hunting for stands beside the
	// control's name, as it does in the English. German parks the verb at the
	// end instead of leading with it, which is why the halves are not a
	// word-for-word split of that English. Read straight through with the glyph
	// gone — it is aria-hidden, so that IS what a screen reader says — each pair
	// still names the control.
	"installApp.steps.iosShare.share.before":
		"In der Symbolleiste des Browsers auf die",
	"installApp.steps.iosShare.share.after": "Teilen-Taste tippen",
	"installApp.steps.iosShare.choose": "„Zu Home-Bildschirm hinzufügen“ wählen",
	"installApp.steps.iosShare.confirm": "Auf „Hinzufügen“ tippen",
	"installApp.steps.macosDock.menu.before":
		"Menü „Ablage“ öffnen oder in der Symbolleiste auf die",
	"installApp.steps.macosDock.menu.after": "Teilen-Taste klicken",
	"installApp.steps.macosDock.choose": "„Zum Dock hinzufügen“ wählen",
	"installApp.steps.macosDock.confirm":
		"Namen eingeben und auf „Hinzufügen“ klicken",
	"installApp.steps.menuShortcut.menu": "Browser-Menü öffnen",
	"installApp.steps.menuShortcut.choose":
		"„Zum Startbildschirm hinzufügen“ wählen",
	"installApp.steps.menuShortcut.confirm":
		"Namen bestätigen und auf „Hinzufügen“ tippen",
	"overlay.restoreViewer": "Ansicht wiederherstellen",
	"overlay.maximizeViewer": "Ansicht maximieren",
	"overlay.closeViewer": "Ansicht schließen",
	"messageComposer.message": "Nachricht",
	"messageComposer.send": "Nachricht senden",
	"window.switcher": "Offene Fenster",
	"window.unnamed": "Fenster",
	"window.details": "Aufklappbarer Inhalt",
	"window.moveHint": "Zum Verschieben ziehen, zum Ausklappen klicken",
	"window.close": "Fenster schließen",
	"window.closeTab": "{{label}} schließen",
	"window.collapse": "Fenster einklappen",
	"window.expand": "Fenster ausklappen",
	"fileStack.files": "{{count}} Dateien",
	"fileStack.open": "{{count}} Dateien anzeigen",
	"fileStack.close": "Dateien einklappen",
	"csv.loading": "CSV-Vorschau wird geladen",
	"csv.failed": "CSV-Vorschau konnte nicht geladen werden",
	"csv.retry": "Erneut versuchen",
	"csv.empty": "Diese CSV-Datei ist leer",
	"csv.search": "Zeilen durchsuchen",
	"csv.count": "{{count}} Zeilen · {{columns}} Spalten",
	"csv.matches": "{{count}} von {{total}} Zeilen · {{columns}} Spalten",
	"csv.wrap": "Text umbrechen",
	"csv.header": "Erste Zeile als Überschrift",
	"csv.preview": "CSV-Vorschau",
	"csv.row": "Originalzeile",
	"csv.column": "Spalte {{number}}",
	"csv.noMatches": "Keine passenden Zeilen",
	"csv.clear": "Suche leeren",
	"xml.loading": "XML-Dokument wird gelesen",
	"xml.failed": "XML-Vorschau nicht verfügbar",
	"xml.retry": "Vorschau erneut laden",
	"xml.unsafe":
		"Diese XML-Datei enthält externe Anweisungen oder Entitäten und kann nicht sicher angezeigt werden. Die Originaldatei in einer Rechnungssoftware öffnen.",
	"xml.malformed":
		"Diese Datei ist kein wohlgeformtes XML. Die Originaldatei beim Absender prüfen lassen.",
	"xml.tooLarge":
		"Diese XML-Datei überschreitet die Vorschaugrenze von 2 MiB. Die Originaldatei in einer Rechnungssoftware öffnen.",
	"xml.unreadable":
		"Die Textkodierung konnte nicht gelesen werden. Die Originaldatei beim Absender prüfen lassen.",
	"xml.requestFailed":
		"Die XML-Originaldatei konnte nicht geladen werden. Verbindung prüfen und erneut versuchen.",
	"xml.view": "XML-Dokumentansicht",
	"xml.invoiceView": "Rechnung",
	"xml.source": "XML-Quelltext",
	"xml.sourceDescription": "Originaler XML-Text",
	"xml.invoice": "Rechnung",
	"xml.document": "Dokument",
	"xml.documentType": "Dokumentart-Code",
	"xml.creditNote": "Gutschrift",
	"xml.numberMissing": "Rechnungsnummer fehlt",
	"xml.nameMissing": "Name fehlt",
	"xml.issued": "Ausgestellt am",
	"xml.dueDate": "Fällig am",
	"xml.amountDue": "Zahlbetrag",
	"xml.seller": "Verkäufer",
	"xml.buyer": "Käufer",
	"xml.vatId": "USt-IdNr.",
	"xml.taxId": "Steuernummer",
	"xml.buyerReference": "Käuferreferenz",
	"xml.orderReference": "Bestellreferenz",
	"xml.items": "Rechnungspositionen",
	"xml.description": "Beschreibung",
	"xml.quantity": "Menge",
	"xml.unitPrice": "Einzelpreis",
	"xml.taxRate": "Steuer",
	"xml.lineNet": "Nettobetrag",
	"xml.noItems":
		"Keine Rechnungspositionen gefunden. Das vollständige Dokument steht im XML-Quelltext.",
	"xml.itemRange": "Positionen {{start}}–{{end}} von {{total}}",
	"xml.previousItems": "Vorherige Positionen",
	"xml.nextItems": "Nächste Positionen",
	"xml.payment": "Zahlung",
	"xml.paymentMeans": "Zahlungsart-Code",
	"xml.paymentReference": "Zahlungsreferenz",
	"xml.paymentTerms": "Zahlungsbedingungen",
	"xml.iban": "IBAN",
	"xml.bic": "BIC",
	"xml.accountName": "Kontoinhaber",
	"xml.accountId": "Zahlungskonto",
	"xml.bankIdentifier": "Bankkennung",
	"xml.totals": "Rechnungssummen",
	"xml.net": "Nettosumme",
	"xml.tax": "Steuerbetrag",
	"xml.gross": "Bruttosumme",
	"xml.prepaid": "Bereits gezahlt",
	"xml.allowance": "Nachlässe auf Dokumentebene",
	"xml.charge": "Zuschläge auf Dokumentebene",
	"xml.taxBreakdown": "Steueraufschlüsselung",
	"xml.taxBasis": "Bemessungsgrundlage",
	"xml.notes": "Hinweise",
	"xml.readingNotice":
		"Die Werte stammen aus der XML-Datei. Diese Vorschau prüft die Rechnung nicht; das vollständige Dokument steht im XML-Quelltext.",
	"xml.thumbnail": "XML-Vorschau von {{name}}",
	"zoomStage.document": "Dokument",
	"zoomStage.image": "Bild",
	"zoomStage.file": "Datei",
	"zoomStage.failed": "Diese Datei konnte nicht gelesen werden.",
	"zoomStage.decodeFailed": "Die Datei konnte nicht dekodiert werden.",
	"zoomStage.loading": "{{name}} wird geladen",
	"zoomStage.pages": "{{name}}, {{count}} Seiten",
	"zoomStage.page": "Seite {{page}} von {{total}}",
	"zoomStage.zoomed": "Auf {{percent}} Prozent vergrößert",
	"zoomStage.zoomIn": "Vergrößern",
	"zoomStage.zoomOut": "Verkleinern",
	"zoomStage.keys":
		"Mit zwei Fingern oder einem Doppeltippen zoomen. Zum Verschieben ziehen. Plus und Minus ändern den Zoom, Null passt die Ansicht ein, Pfeil- und Bildtasten blättern durch die Datei.",
	"dateRange.presets": "Vorgegebene Zeiträume",
	"dateField.save": "Speichern",
	"dateField.cancel": "Abbrechen",
	"dateField.preset.now": "Jetzt",
	"dateField.preset.today": "Heute",
	"dateField.outOfRange.tooEarly": "Frühestens erlaubt: {{date}}",
	"dateField.outOfRange.tooLate": "Spätestens erlaubt: {{date}}",
	"dateField.useEarliest": "{{date}} übernehmen",
	"dateField.useLatest": "{{date}} übernehmen",
	"dateField.timeOfDay": "Uhrzeit",
	"dateField.dateEntry": "Datum (JJJJ-MM-TT)",
	"dateField.dateEntryPlaceholder": "JJJJ-MM-TT",
	"dateField.invalidDate": "Gültiges Datum im Format JJJJ-MM-TT eingeben.",
	"timePicker.hours": "Stunde",
	"timePicker.minutes": "Minuten",
	"timePicker.seconds": "Sekunden",
	"checkbox.error.valueMissing":
		"Zum Fortfahren muss dieses Kästchen aktiviert sein.",
	"switch.error.valueMissing": "Dieser Schalter muss aktiviert sein.",
	"dropzone.add": "Hinzufügen",
	"attachments.refusal.oneAtATime":
		"Ein Bild nach dem anderen — {{name}} wurde nicht angehängt.",
	"form.unanswered":
		"Es kam nicht rechtzeitig eine Antwort. Der Vorgang kann trotzdem ausgeführt worden sein — vor dem erneuten Senden prüfen.",
	"reader.measure.words.one": "{{count}} Wort",
	"reader.measure.words.other": "{{count}} Wörter",
	"reader.view.source": "Quelle",
	"reader.view.sourceNamed": "Quelle von {{subject}}",
	"markdown.image": "Bild: {{alt}}",
	"markdown.imageUnnamed": "Bild",
	"markdown.json": "JSON",
	"markdown.diagramFailed":
		"Dieses Diagramm konnte nicht dargestellt werden. Der Quelltext steht darunter.",
	"navigationRail.landmark": "Hauptnavigation",
	"navigationRail.tablist": "Navigation",
	"breadcrumb.landmark": "Navigationspfad",

	// <schmancy-execution-graph> and its inspector. Status, availability and
	// kind are read inside one "status · availability" line, so the adjectives
	// stay lowercase there and the kinds, being nouns, do not.
	"execution.map": "Ablaufkarte",
	"execution.mapDescription":
		"Definitionen und beobachtete Übergänge im Ablauf.",
	"execution.keys":
		"Zum Verschieben ziehen oder scrollen. Mit zwei Fingern oder Plus und Minus zoomen. Null passt die Ansicht ein.",
	"execution.zoomIn": "{{name}} vergrößern",
	"execution.zoomOut": "{{name}} verkleinern",
	"execution.fit": "{{name}} einpassen",
	"execution.nothingReached": "Noch nichts ausgeführt",
	"execution.details": "Ablaufdetails zu {{name}}",
	"execution.close": "Knotendetails schließen",
	"execution.definitionOnly": "Nicht ausgeführt · nur Definition",
	"execution.attempt": "{{status}} · Versuch {{attempt}}",
	"execution.contract": "Festgelegter Eingabevertrag",
	"execution.occurrences": "Erreichte Durchläufe",
	"execution.flow": "Eingabe → Ziel, Entscheidung und Belege → Ausgabe",
	"execution.flowDescription": "Eingabe, Entscheidung und Ausgabe",
	"execution.input": "Eingabe",
	"execution.output": "Ausgabe",
	"execution.goal": "Ziel",
	"execution.decision": "Zusammenfassung der Entscheidung",
	"execution.evidence": "Belege",
	"execution.prompt": "Prompt",
	"execution.error": "Fehler",
	"execution.unrecorded": "Nicht erfasst",
	"execution.noInput": "Nicht ausgeführt · keine Eingabe erfasst",
	"execution.noOutput": "Nicht ausgeführt · keine Ausgabe erfasst",
	"execution.metadata":
		"Modell: {{model}} · Dauer: {{duration}} · Verbrauch: {{usage}}",
	"execution.duration": "{{duration}} ms",
	"execution.payloadSubject": "{{name}} · {{field}}",
	"execution.status.pending": "ausstehend",
	"execution.status.running": "läuft",
	"execution.status.completed": "abgeschlossen",
	"execution.status.waiting": "wartet",
	"execution.status.failed": "fehlgeschlagen",
	"execution.availability.available": "verfügbar",
	"execution.availability.dormant": "ruhend",
	"execution.kind.input": "Eingabe",
	"execution.kind.read": "Lesezugriff",
	"execution.kind.agent": "Agent",
	"execution.kind.capability": "Fähigkeit",
	"execution.kind.tool": "Werkzeug",
	"execution.kind.model": "Modell",
	"execution.kind.memory": "Gedächtnis",
	"execution.kind.question": "Rückfrage",
	"execution.kind.effect": "Wirkung",
	"execution.kind.receipt": "Quittung",
	"execution.kind.runtime": "Laufzeit",
};
